import { h, createContext } from 'preact';
import { generateUuidV7 } from '../utils/uuid';
import { useContext, useState, useEffect, useCallback, useMemo, useRef } from 'preact/hooks';
import { URLS } from '../Urls';
import { clearPromoCode, getPromoRecord } from '../promo/promoStorage';
import { oNumberMatches } from '../promo/promoParams';
import { notifyPromoRejected } from '../promo/promoEvents';
import { getCountryParamFromUrl } from './pdpData';
import { refreshFreeShippingThreshold } from '../cart/freeShipping';
import {
  fetchCart,
  addToCart as apiAddToCart,
  updateCartItem,
  removeFromCart,
  fetchProductByNumber,
  fetchProductsByNumbers as apiFetchProductsByNumbers,
  fetchPaymentMethods as apiFetchPaymentMethods,
  fetchOrders as apiFetchOrders,
  downloadDocument as apiDownloadDocument,
  triggerDocumentDownload as apiTriggerDocumentDownload,
  getContextToken as getContextTokenFromApi,
  getEstablishedCustomerToken,
  rememberEstablishedCustomerToken,
  login as apiLogin,
  logout as apiLogout,
  fetchCustomer as apiFetchCustomer,
  type Cart,
  type Product,
  type PaymentMethod,
  type Order,
  type Document,
  type Customer,
} from './shopwareApi';
import { getCookie, setCookie, removeCookie } from '../utils/cookieStorage';

const CONTEXT_TOKEN_KEY = 'sw-context-token';
const CART_COUNT_KEY = 'sw-cart-count';
const APP_USER_AGENT = 'com.orthomol.b2c';

declare global {
  interface Window {
    OrthomolApp?: {
      postMessage: (message: string) => void;
    };
    // Cookiebot consent API, loaded site-wide by the AppBundle tracking
    // templates. We only read the marketing consent flag before storing or
    // forwarding UTM. In the app WebView this reflects the consent the app
    // forwards via `?b2c_cmp_market=` (submitted through submitCustomConsent).
    Cookiebot?: {
      // `hasResponse` is Cookiebot's "the user has submitted a consent response"
      // flag and is the reliable "consent resolved" signal. The `consent` object
      // alone is populated with defaults (marketing=false) before the user
      // responds, so it must not be used to infer a denial.
      hasResponse?: boolean;
      consent?: {
        marketing?: boolean;
        statistics?: boolean;
        preferences?: boolean;
      };
    };
  }
}

// WebView runtimes (WKWebView, Android WebView) append custom identifiers to
// the default browser UA rather than replacing it, so check for substring
// presence instead of strict equality.
const isOrthomolApp = (): boolean =>
  typeof navigator !== 'undefined' && navigator.userAgent.includes(APP_USER_AGENT);

const hasOrthomolAppBridge = (): boolean =>
  typeof window !== 'undefined' && typeof window.OrthomolApp?.postMessage === 'function';

// App-origin attribution cookies planted by the shop-launch consume step
// (B2cAppShopLaunchController::applyAttributionCookies). Forwarded as query
// params onto the Shopware checkout URL for server-side analytics. Maps the
// cookie name to the checkout query-param name.
const ATTRIBUTION_COOKIE_TO_PARAM: Record<string, string> = {
  b2c_surface: 'surface',
  b2c_source: 'source',
  b2c_platform: 'platform',
};

// Persisted marketing attribution. The app opens PDPs in the WebView with
// `?utm_source=inapp&utm_medium=app&utm_campaign=startPage`, but the user may
// browse to other pages (full reloads that drop the query) before tapping the
// checkout button. We snapshot the landing UTM params into a session cookie so
// they survive those navigations and are still present when the checkout
// handoff builds its target URL (ORT-3140). Session-scoped so a past campaign
// doesn't linger into a later, unrelated visit.
//
// This is a MARKETING cookie: it is only written (and forwarded) with marketing
// consent (see hasMarketingConsent), and is actively deleted when consent is
// absent or withdrawn (see syncUtmConsent, wired to CookiebotOnDecline). We do
// NOT rely on Cookiebot's own removeCookies() for cleanup: in the app (B2C) path
// the decline handler in AppBundle/tracking.html.twig returns early on
// `b2cHasConsent` before reaching removeCookies(), so a stale snapshot would
// otherwise survive a withdrawal.
const UTM_PARAMS_COOKIE = 'b2c_utm';

/**
 * Marketing-consent gate. UTM params are marketing campaign attribution, so we
 * only store or forward them when the user has granted marketing consent.
 * Reuses the site-wide Cookiebot consent API rather than inventing a new consent
 * mechanism; in the app WebView this reflects the choice the app forwards via
 * `?b2c_cmp_market=`. Returns false when Cookiebot has not resolved consent yet.
 */
function hasMarketingConsent(): boolean {
  return typeof window !== 'undefined' && window.Cookiebot?.consent?.marketing === true;
}

/**
 * True only once Cookiebot has RESOLVED consent and marketing was NOT granted.
 * Distinct from `!hasMarketingConsent()`, which is also true while consent is
 * still unresolved — we must not delete a valid snapshot in that "unknown"
 * window, only on an explicit denial/withdrawal.
 *
 * Gated on `hasResponse` (the user has actually submitted a response), NOT on
 * `consent != null`: Cookiebot populates `consent` with defaults (marketing =
 * false) before `CookiebotOnConsentReady`, so `consent != null` reports a false
 * denial in the pre-response window and the mount reconcile would wipe a still
 * valid snapshot (ORT-3140 review).
 */
function marketingConsentDenied(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.Cookiebot?.hasResponse === true &&
    window.Cookiebot.consent?.marketing !== true
  );
}

/**
 * Pull the `utm_*` params out of a query string, dropping empty values. Used
 * both for the live page URL and for the persisted snapshot. Any `utm_*` key is
 * kept generically so utm_term/utm_content and future additions carry through.
 */
function extractUtmParams(search: string): URLSearchParams {
  const utm = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(search).entries()) {
    if (key.toLowerCase().startsWith('utm_') && value !== '') {
      // `set` (not `append`): a page carries one value per UTM key, so last
      // wins keeps the query tidy and avoids duplicate keys on the target URL.
      utm.set(key, value);
    }
  }
  return utm;
}

/**
 * Snapshot the current page's UTM params into the session cookie. Called on
 * shop mount so a campaign landing is captured before the user navigates on.
 * No-op without marketing consent (it's a marketing cookie). Only writes when
 * the URL actually carries UTM params, so an organic page view never clears a
 * snapshot taken on an earlier campaign landing (last-touch).
 */
function persistUtmParamsFromUrl(): void {
  if (typeof window === 'undefined' || !hasMarketingConsent()) {
    return;
  }
  const serialized = extractUtmParams(window.location.search).toString();
  if (serialized !== '') {
    setCookie(UTM_PARAMS_COOKIE, serialized, { days: 0 }); // days:0 => session cookie
  }
}

/**
 * Marketing (UTM) params to forward onto the checkout handoff. Prefers whatever
 * is live on the current page URL, then falls back to the persisted snapshot so
 * the params survive in-WebView navigation away from the campaign landing page
 * (ORT-3140). Returns nothing without marketing consent, so UTM is neither
 * stored nor forwarded when the user declined marketing.
 */
function collectUtmParams(): URLSearchParams {
  if (typeof window === 'undefined' || !hasMarketingConsent()) {
    return new URLSearchParams();
  }
  const fromUrl = extractUtmParams(window.location.search);
  if (fromUrl.toString() !== '') {
    return fromUrl;
  }
  const stored = getCookie(UTM_PARAMS_COOKIE);
  return stored ? extractUtmParams(stored) : new URLSearchParams();
}

/**
 * Reconcile the persisted UTM snapshot with the current marketing-consent state:
 * capture on grant, and actively delete on an explicit denial/withdrawal. While
 * consent is still unresolved we leave any existing snapshot untouched, so a
 * valid snapshot isn't wiped before Cookiebot is ready. Wired to mount plus the
 * Cookiebot consent events so a withdrawal clears `b2c_utm` even in the app
 * (B2C) path, where Cookiebot's own removeCookies() never runs (see
 * UTM_PARAMS_COOKIE).
 */
function syncUtmConsentToStorage(): void {
  if (hasMarketingConsent()) {
    persistUtmParamsFromUrl();
  } else if (marketingConsentDenied()) {
    removeCookie(UTM_PARAMS_COOKIE);
  }
}

/**
 * Build the Shopware checkout target path, appending whatever app-origin
 * attribution cookies are present (?surface=&source=&platform=), plus any UTM
 * params from the current page URL (ORT-3140) and the AT test toggle
 * (ORT-3064: ?country=at -> ?shippingTo=AT, so the guest cart stays on AT).
 * `base` defaults to `/checkout/register/` (correct for the plain-web/guest
 * path); the app handoff passes `/checkout/confirm` because it is always a
 * logged-in customer and the `register -> confirm` redirect Shopware would
 * otherwise run drops the query string (utm included). With no attribution and
 * no campaign params the path stays `base`.
 */
function buildCheckoutTargetPath(base = '/checkout/register/'): string {
  const params = new URLSearchParams();
  for (const [cookieName, paramName] of Object.entries(ATTRIBUTION_COOKIE_TO_PARAM)) {
    const value = getCookie(cookieName);
    if (value) {
      params.set(paramName, value);
    }
  }
  // UTM keys (utm_*) never collide with the attribution keys above
  // (surface/source/platform), so forwarding them is purely additive.
  for (const [key, value] of collectUtmParams().entries()) {
    params.set(key, value);
  }
  // ORT-3064: carry the AT test toggle (?country=at) through as ?shippingTo=AT.
  const shippingTo = getCountryParamFromUrl();
  if (shippingTo) {
    params.set('shippingTo', shippingTo);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

// Diagnostic codes surfaced in the checkout alert (and console) so a failure
// can be pinned down straight from a screen recording. The app forwards only
// recordings, not console or network logs, so the code has to be visible on
// screen. Keep these strings short and stable; do not repurpose an existing one.
type CheckoutHandoffErrorCode =
  | 'E-CHK-NET' //     fetch never reached the backend (offline, DNS, TLS, reset)
  | 'E-CHK-TIMEOUT' // request aborted after the handoff timeout elapsed
  | 'E-CHK-HTTP' //    backend responded with a non-2xx status (status appended)
  | 'E-CHK-EMPTY' //   backend responded 2xx but without a usable launchUrl
  | 'E-CHK-BRIDGE' //  native postMessage bridge threw during the handoff
  | 'E-CHK-NOCART'; // no cart or context token available to start checkout

// Fail a stalled handoff request instead of leaving the checkout button dead.
// The reported symptom is mobile-network-only, where a request can hang without
// ever rejecting; this converts that into a visible E-CHK-TIMEOUT.
const CHECKOUT_HANDOFF_TIMEOUT_MS = 15000;

class CheckoutHandoffError extends Error {
  readonly code: CheckoutHandoffErrorCode;
  readonly httpStatus?: number;
  readonly originalError?: unknown;

  constructor(
    code: CheckoutHandoffErrorCode,
    message: string,
    options: { httpStatus?: number; originalError?: unknown } = {}
  ) {
    super(message);
    this.name = 'CheckoutHandoffError';
    this.code = code;
    this.httpStatus = options.httpStatus;
    this.originalError = options.originalError;
  }

  /** Short label shown to the user, e.g. "E-CHK-HTTP-502". */
  get displayCode(): string {
    return this.code === 'E-CHK-HTTP' && this.httpStatus != null
      ? `${this.code}-${this.httpStatus}`
      : this.code;
  }
}

/** Resolve any thrown value to a user-visible diagnostic code. */
function toCheckoutDisplayCode(error: unknown): string {
  return error instanceof CheckoutHandoffError ? error.displayCode : 'E-CHK-UNKNOWN';
}

async function requestCheckoutLaunchUrl(): Promise<string> {
  // The server reads the sw-context-token from the request cookie (sent via
  // credentials: 'include') so the client no longer passes it in the body,
  // which avoids trusting a client-supplied context token.
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), CHECKOUT_HANDOFF_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(URLS.meinOrthomolShopLaunch, {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        surface: 'browser',
        // Send the neutral /checkout/register default. The backend (launchBrowser)
        // upgrades it to /checkout/confirm when the customer has a default shipping
        // address, skipping the register -> confirm redirect that would otherwise
        // strip the query (utm) off the URL. Backend owns the decision because it
        // holds the customer's Shopware context token.
        targetPath: buildCheckoutTargetPath(),
      }),
    });
  } catch (error) {
    // fetch() only rejects on network-level failures, never on HTTP status.
    // An AbortError means our timeout fired (stalled connection); anything
    // else is a genuine connectivity failure.
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new CheckoutHandoffError(
        'E-CHK-TIMEOUT',
        `Shop-launch request timed out after ${CHECKOUT_HANDOFF_TIMEOUT_MS} ms`,
        { originalError: error }
      );
    }
    throw new CheckoutHandoffError('E-CHK-NET', 'Shop-launch request did not reach the backend', {
      originalError: error,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new CheckoutHandoffError(
      'E-CHK-HTTP',
      data.error || `Shop-launch responded with status ${response.status}`,
      { httpStatus: response.status }
    );
  }
  if (typeof data.launchUrl !== 'string' || data.launchUrl === '') {
    throw new CheckoutHandoffError('E-CHK-EMPTY', 'Shop-launch response contained no launchUrl');
  }

  return data.launchUrl;
}

interface ShopwareContextValue {
  // Drawer state
  isDrawerOpened: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;

  // Cart state
  cart: Cart | null;
  isAddingToCart: boolean;
  lastAddedProductId: string | null;

  // Cart operations
  refreshCart: () => Promise<Cart>;
  addProduct: (productId: string, quantity?: number, promoScopeONumber?: string | null) => Promise<Cart | null>;
  addMultipleProducts: (products: Array<{ productId: string; quantity: number }>) => Promise<Cart | null>;
  updateQuantity: (lineItemId: string, quantity: number) => Promise<Cart>;
  removeProduct: (lineItemId: string) => Promise<Cart>;
  clearCart: () => void;
  proceedToCheckout: () => Promise<void>;

  // Product operations
  fetchProduct: (productNumber: string) => Promise<Product | null>;
  fetchProductsByNumbers: (productNumbers: string[]) => Promise<Map<string, Product>>;

  // Payment methods operations
  fetchPaymentMethods: () => Promise<PaymentMethod[]>;

  // Order operations
  fetchOrders: (body?: {
    limit?: number;
    page?: number;
    filter?: any[];
    sort?: any[];
    associations?: any;
    aggregations?: any[]; // Array of aggregation objects with { name, type, field }
    'total-count-mode'?: 'none' | 'exact' | 'next-pages';
  }) => Promise<{ elements: Order[]; total: number; aggregations?: any }>;

  // Document operations
  downloadDocument: (documentId: string, deepLinkCode: string) => Promise<Blob>;
  triggerDocumentDownload: (documentId: string, deepLinkCode: string, filename?: string) => Promise<void>;

  // Authentication operations
  login: () => Promise<void>;
  logout: () => Promise<void>;

  // Customer operations
  fetchCustomer: () => Promise<Customer>;
}

const ShopwareContext = createContext<ShopwareContextValue | null>(null);

export const useShopware = () => {
  const context = useContext(ShopwareContext);
  if (!context) {
    throw new Error('useShopware must be used within a ShopwareProvider');
  }
  return context;
};

interface ShopwareProviderProps {
  children: preact.ComponentChildren;
}

export const ShopwareProvider = ({ children }: ShopwareProviderProps) => {
  // Drawer state
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);

  // Cart state
  const [cart, setCart] = useState<Cart | null>(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [lastAddedProductId, setLastAddedProductId] = useState<string | null>(null);

  // Update shopping cart badge count whenever cart changes
  useEffect(() => {
    const cartLink = document.getElementById('shopping-cart-link');
    if (cartLink) {
      const oldItemCount = getCookie(CART_COUNT_KEY);
      if (oldItemCount && cart === null) {
        const parsedOldCount = parseInt(oldItemCount, 10);
        // Note: bugbot complained that this prevents the cart from being cleared, which is incorrect, because if the cart is cleared, the `cart` var will never be null.
        if (!isNaN(parsedOldCount) && parsedOldCount > 0) {
          return;
        }
      }

      // Calculate total quantity across all line items (not just the count of different products)
      // Filter out promotional items from the count
      const itemCount =
        cart?.lineItems?.reduce((total, lineItem) => {
          // Skip promotional items
          if (lineItem.type === 'promotion') {
            return total;
          }
          return total + (lineItem.quantity || 0);
        }, 0) || 0;

      cartLink.setAttribute('data-item-count', itemCount.toString());

      // Also save to cookie so it's available on pages without Preact/Shopware
      try {
        setCookie(CART_COUNT_KEY, itemCount.toString());
      } catch (e) {
        console.warn('[ShopwareProvider] Failed to save cart count to cookie:', e);
      }
    }
  }, [cart]);

  // Keep the UTM snapshot (b2c_utm) in lockstep with marketing consent (ORT-3140):
  // capture on a UTM-tagged PDP landing before the user navigates on, and delete
  // it on an explicit denial/withdrawal. Cookiebot may not have resolved consent
  // at mount (esp. on the web where the banner is still pending), so reconcile
  // again on every consent event, while still on the landing PDP whose URL
  // carries the UTM.
  useEffect(() => {
    syncUtmConsentToStorage();
    window.addEventListener('CookiebotOnConsentReady', syncUtmConsentToStorage);
    window.addEventListener('CookiebotOnAccept', syncUtmConsentToStorage);
    window.addEventListener('CookiebotOnDecline', syncUtmConsentToStorage);
    return () => {
      window.removeEventListener('CookiebotOnConsentReady', syncUtmConsentToStorage);
      window.removeEventListener('CookiebotOnAccept', syncUtmConsentToStorage);
      window.removeEventListener('CookiebotOnDecline', syncUtmConsentToStorage);
    };
  }, []);

  // Drawer operations
  const openDrawer = useCallback(() => setIsDrawerOpened(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpened(false), []);

  // ==================== CART OPERATIONS ====================

  /**
   * Fetches the current cart from the API
   */
  const refreshCart = useCallback(async () => {
    try {
      const cartData = await fetchCart();
      setCart(cartData);
      return cartData;
    } catch (error) {
      console.error('[ShopwareProvider] Failed to fetch cart:', error);
      throw error;
    }
  }, []);

  /**
   * Updates the quantity of a cart line item
   */
  const updateQuantity = useCallback(async (lineItemId: string, quantity: number) => {
    try {
      const cartData = await updateCartItem({
        items: [
          {
            id: lineItemId,
            quantity,
          },
        ],
      });

      setCart(cartData);
      return cartData;
    } catch (error) {
      console.error('[ShopwareProvider] Failed to update cart item:', error);
      throw error;
    }
  }, []);

  /**
   * Adds a product to the cart
   * If the product already exists in the cart, updates the quantity instead
   */
  const addProduct = useCallback(
    async (productId: string, quantity: number = 1, promoScopeONumber?: string | null) => {
      // Prevent concurrent add operations. Return null, not the current cart: a resolved cart made the
      // skipped call indistinguishable from a successful add for callers that act on the result (the
      // add-to-cart islands opened the drawer and pushed a GA4 add_to_cart for an item never added).
      // null = "nothing was added"; a successful add always resolves to the updated cart object.
      if (isAddingToCart) {
        console.warn('[ShopwareProvider] Add to cart operation already in progress');
        return null;
      }

      try {
        setIsAddingToCart(true);

        // Track this as the last added product
        setLastAddedProductId(productId);

        // ORT-3064 test toggle: when the PDP was opened with ?country=at, switch the (guest)
        // cart to that country on add-to-cart so prices/promotions stay consistent end-to-end.
        const shippingTo = getCountryParamFromUrl() ?? undefined;

        // ORT-3178: Shopware keys line items by id, and a new product line gets a random id below, so
        // an add merges only if the cart state already holds the product. Off the PDP that state is
        // null until the drawer mounts, and the PDP opens the drawer before the add, so the first add
        // per page load missed the existing line and created a second one. Load the cart first when
        // the state is null. A failed fetch does not block the add (it then adds a new line, as before).
        const currentCart =
          cart ??
          (await fetchCart().catch((err) => {
            console.warn('[ShopwareProvider] Failed to load cart before add, adding as a new line item:', err);
            return null;
          }));

        // A pending Newsletter promo applies to *this* cart action regardless
        // of whether we end up adding a new product line item or just bumping
        // the quantity of an existing one. Resolve it up-front so both branches
        // honor it consistently.
        const pendingPromoRecord = getPromoRecord();
        // ORT-3077: a code scoped by explicit o-numbers (?pron=) applies only to those products.
        // Gate the attachment here so adding a product that is NOT in the list never applies the
        // code in the cart. Enforcing the pron restriction end-to-end is the point of the feature:
        // Shopware does not know about pron, so a broadly-scoped promotion would otherwise still be
        // granted on a non-matching product. A record without oNumbers stays unrestricted (Stage-1
        // behavior). If a restricted code has no o-number to check against, we do NOT attach it
        // (fail safe). The record is left in storage either way, so the code still applies once a
        // matching product is added.
        const pronONumbers = pendingPromoRecord?.oNumbers ?? null;
        const pronRestricts = !!pronONumbers && pronONumbers.length > 0;
        // ORT-3152: entries may be wildcard patterns (`M*`, `O611*`), so membership is resolved by
        // oNumberMatches, which the banner uses too. Both gates stay in lockstep that way.
        const productInPron =
          promoScopeONumber != null && pronRestricts && oNumberMatches(pronONumbers!, promoScopeONumber);
        const pendingPromoCode =
          pendingPromoRecord?.code && (!pronRestricts || productInPron) ? pendingPromoRecord.code : null;

        // pra=replace: the Newsletter explicitly asked that this code take over
        // from any existing promotion codes in the cart. Strip them first so we
        // start from a clean promotion slate. We exclude the pending code from
        // the wipe set — re-removing-then-re-adding the same code is needless
        // churn that Shopware would happily accept but produces a confusing
        // intermediate cart state. Skip entirely when the code is gated out above
        // (don't wipe existing promotions if we're not attaching a newsletter code).
        if (pendingPromoCode && pendingPromoRecord?.applyMode === 'replace' && currentCart?.lineItems?.length) {
          const existingPromotionIds = currentCart.lineItems
            .filter((li) => li.type === 'promotion' && li.id && li.referencedId !== pendingPromoCode)
            .map((li) => li.id as string);
          if (existingPromotionIds.length > 0) {
            try {
              await removeFromCart(existingPromotionIds);
            } catch (err) {
              // Don't block the add-to-cart on a failed wipe — worst case the
              // user ends up with both the manual and the Newsletter promo,
              // which is the same outcome as pra=add.
              console.warn('[ShopwareProvider] Failed to clear existing promotions for replace mode:', err);
            }
          }
        }

        // Check if product already exists in cart
        const existingLineItem = currentCart?.lineItems?.find((lineItem) => lineItem.referencedId === productId);
        // Has this exact promotion code already been applied? Re-adding it is a
        // no-op for Shopware but pointless — and we'd then have to inspect the
        // response for droppedPromotionCodes anyway. Skip the extra request.
        const promoAlreadyInCart = pendingPromoCode != null && (currentCart?.lineItems ?? []).some(
          (li) => li.type === 'promotion' && li.referencedId === pendingPromoCode
        );

        let cartData;
        if (existingLineItem && existingLineItem.id) {
          // Product exists — update quantity. The promotion (if any new one is
          // pending) is attached via a separate request below, because
          // updateLineItem doesn't accept new line items.
          console.log('[eshop] [ShopwareProvider] Product already in cart, updating quantity');
          const newQuantity = (existingLineItem.quantity || 1) + quantity;
          // Use the raw API here rather than updateQuantity(): the latter calls
          // setCart() internally, which would render an intermediate cart that
          // doesn't yet include the pending promotion (flashing the badge/drawer
          // with promo-less state). We commit once via setCart(cartData) below,
          // after the promotion has been attached.
          cartData = await updateCartItem(
            {
              items: [{ id: existingLineItem.id, quantity: newQuantity }],
            },
            shippingTo
          );

          if (pendingPromoCode && !promoAlreadyInCart) {
            cartData = await apiAddToCart(
              {
                items: [{ type: 'promotion', referencedId: pendingPromoCode }],
              },
              shippingTo
            );
          }
        } else {
          // New product line item — bundle product + promotion in one request
          // so a still-empty cart doesn't drop the promotion before we add the
          // product.
          const lineItemId = generateUuidV7();
          const items: any[] = [
            {
              id: lineItemId,
              type: 'product',
              referencedId: productId,
              quantity,
            },
          ];
          if (pendingPromoCode && !promoAlreadyInCart) {
            items.push({ type: 'promotion', referencedId: pendingPromoCode });
          }
          cartData = await apiAddToCart({ items }, shippingTo);
        }

        // If Shopware rejected the promo (wrong product scope, expired, …),
        // it surfaces in `extensions.droppedPromotionCodes`. Clear our stored
        // code in that case so we don't keep re-attempting a dead promo on
        // every subsequent add-to-cart.
        if (pendingPromoCode) {
          const dropped = (cartData as any)?.extensions?.droppedPromotionCodes;
          if (dropped && Object.prototype.hasOwnProperty.call(dropped, pendingPromoCode)) {
            clearPromoCode();
            notifyPromoRejected(pendingPromoCode);
          }
        }

        setCart(cartData);
        return cartData;
      } catch (error) {
        console.error('[ShopwareProvider] Failed to add product to cart:', error);
        throw error;
      } finally {
        setIsAddingToCart(false);
      }
    },
    [cart, isAddingToCart]
  );

  /**
   * Adds multiple products to the cart in a single API call
   * Useful for reordering or bulk add operations
   */
  const addMultipleProducts = useCallback(
    async (products: Array<{ productId: string; quantity: number }>) => {
      // Prevent concurrent add operations
      if (isAddingToCart) {
        console.warn('[ShopwareProvider] Add to cart operation already in progress');
        return cart;
      }

      try {
        setIsAddingToCart(true);
        // ORT-3178: same as addProduct. "Meine Bestellungen" does not load the cart, so without this a
        // reorder added every product already in the cart as a second line.
        const currentCart =
          cart ??
          (await fetchCart().catch((err) => {
            console.warn('[ShopwareProvider] Failed to load cart before reorder, adding as new line items:', err);
            return null;
          }));
        // Build line items array for all products
        const items = products.map((product) => {
          // Check if product already exists in cart
          const existingLineItem = currentCart?.lineItems?.find((lineItem) => lineItem.referencedId === product.productId);

          if (existingLineItem && existingLineItem.id) {
            // Product exists, add the new quantity (API will add to existing)
            return {
              id: existingLineItem.id,
              type: 'product' as const,
              referencedId: product.productId,
              quantity: product.quantity, // Just the amount to add (not total)
            };
          } else {
            // Product doesn't exist, create new line item
            const lineItemId = generateUuidV7();
            return {
              id: lineItemId,
              type: 'product' as const,
              referencedId: product.productId,
              quantity: product.quantity,
            };
          }
        });

        console.log('[eshop] [ShopwareProvider] Adding multiple products to cart:', items);

        const cartData = await apiAddToCart({
          items,
        });

        setCart(cartData);
        return cartData;
      } catch (error) {
        console.error('[ShopwareProvider] Failed to add multiple products to cart:', error);
        throw error;
      } finally {
        setIsAddingToCart(false);
      }
    },
    [cart, isAddingToCart]
  );

  /**
   * Removes a product from the cart
   */
  const removeProduct = useCallback(async (lineItemId: string) => {
    try {
      const cartData = await removeFromCart([lineItemId]);
      setCart(cartData);
      return cartData;
    } catch (error) {
      console.error('[ShopwareProvider] Failed to remove product from cart:', error);
      throw error;
    }
  }, []);

  /**
   * Clears the cart by removing the context token
   */
  const clearCart = useCallback(() => {
    removeCookie(CONTEXT_TOKEN_KEY);
    setCart(null);
  }, []);

  /**
   * Proceeds to Shopware checkout.
   *
   * Guarded against concurrent invocations — the app-path requests a
   * one-time launch URL and fires a postMessage to the native bridge;
   * rapid clicks without this guard would mint multiple codes and open
   * several external browser windows.
   */
  const isProceedingToCheckoutRef = useRef<boolean>(false);
  const proceedToCheckout = useCallback(async () => {
    if (isProceedingToCheckoutRef.current) {
      return;
    }

    const token = getContextTokenFromApi();
    if (!cart || !token) {
      console.error('[ShopwareProvider] Checkout blocked [E-CHK-NOCART]: cart or context token not available');
      alert('Der Checkout konnte nicht gestartet werden (Fehler: E-CHK-NOCART). Bitte lade die Seite neu und versuche es erneut.');
      return;
    }
    console.log('[eshop] [ShopwareProvider] Proceeding to checkout with cart:', cart);

    isProceedingToCheckoutRef.current = true;

    if (isOrthomolApp() && hasOrthomolAppBridge()) {
      try {
        const launchUrl = await requestCheckoutLaunchUrl();
        // Re-read the bridge after the await. hasOrthomolAppBridge() was true
        // when we entered this branch, but the await above is an async gap in
        // which the native layer could tear the bridge down. Optional chaining
        // here would turn that into a silent no-op that falls through to the
        // "success" return below with the guard still latched, leaving the
        // button dead. Treat a missing bridge as an explicit failure instead.
        const bridge = window.OrthomolApp;
        if (!bridge) {
          throw new CheckoutHandoffError('E-CHK-BRIDGE', 'Native bridge unavailable at handoff');
        }
        try {
          bridge.postMessage(
            JSON.stringify({
              type: 'open_external_browser',
              launchUrl,
            })
          );
        } catch (bridgeError) {
          throw new CheckoutHandoffError('E-CHK-BRIDGE', 'Native bridge postMessage threw', {
            originalError: bridgeError,
          });
        }
        // Keep the guard LATCHED after a successful handoff. postMessage is
        // synchronous, so releasing it here would let a second tap — before
        // the native browser visibly opens — mint another one-time launch
        // code and fire a duplicate open_external_browser. The guard is
        // released when the user returns to this frozen webview page (the
        // bfcache pageshow handler below resets it).
        return;
      } catch (error) {
        // Do NOT silently fall back to in-webview checkout: the webview is a
        // restricted surface where the user can't complete checkout, and the
        // fallback would drop them on a login page as if logged out. Surface
        // the error to the user so they can retry.
        const code = toCheckoutDisplayCode(error);
        console.error(`[ShopwareProvider] Checkout handoff failed [${code}]:`, error);
        alert(`Der Checkout konnte nicht im Browser geöffnet werden (Fehler: ${code}). Bitte versuche es erneut.`);
        isProceedingToCheckoutRef.current = false; // allow retry after a failed handoff
        return;
      }
    }

    // Non-app path: a full navigation starts now and unloads the page, so the
    // guard state is moot — reset it anyway in case navigation is blocked.
    window.location.href = buildCheckoutTargetPath(); // Token + attribution read from cookies
    isProceedingToCheckoutRef.current = false;
  }, [cart]);

  // ==================== PRODUCT OPERATIONS ====================

  /**
   * Fetch a single product by its product number (o-number)
   */
  const fetchProduct = useCallback(async (productNumber: string): Promise<Product | null> => {
    try {
      return await fetchProductByNumber(productNumber);
    } catch (error) {
      console.error('[ShopwareProvider] Failed to fetch product:', error);
      throw error;
    }
  }, []);

  /**
   * Fetch multiple products by their product numbers
   * Returns a map of productNumber -> Product for easy lookup
   *
   * Delegates to the API-level `fetchProductsByNumbers` (single source of truth for
   * the filter/limit/mapping logic) and adapts the resulting Record to a Map.
   */
  const fetchProductsByNumbers = useCallback(async (productNumbers: string[]): Promise<Map<string, Product>> => {
    try {
      const record = await apiFetchProductsByNumbers(productNumbers);
      return new Map(Object.entries(record));
    } catch (error) {
      console.error('[ShopwareProvider] Failed to fetch products:', error);
      throw error;
    }
  }, []);

  // ==================== PAYMENT METHODS OPERATIONS ====================

  /**
   * Fetch available payment methods
   * Returns a sorted array of payment methods
   */
  const fetchPaymentMethods = useCallback(async (): Promise<PaymentMethod[]> => {
    try {
      const response = await apiFetchPaymentMethods(true);
      console.log('[eshop] [ShopwareProvider] Payment methods API response:', response);

      // Sort by position and return
      const sortedMethods = (response.elements || []).sort((a, b) => {
        const posA = a.position ?? 0;
        const posB = b.position ?? 0;
        return posA - posB;
      });

      return sortedMethods;
    } catch (error) {
      console.error('[ShopwareProvider] Failed to fetch payment methods:', error);
      throw error;
    }
  }, []);

  // ==================== ORDER OPERATIONS ====================

  /**
   * Fetch list of orders with pagination
   * Default: 5 items per page
   *
   * To include documents with orders, pass associations:
   * @example
   * fetchOrders({ associations: { documents: {} }, 'total-count-mode': 'exact' })
   */
  const fetchOrders = useCallback(
    async (body?: {
      limit?: number;
      page?: number;
      filter?: any[];
      sort?: any[];
      associations?: any;
      aggregations?: any[]; // Array of aggregation objects with { name, type, field }
      'total-count-mode'?: 'none' | 'exact' | 'next-pages';
    }): Promise<{ elements: Order[]; total: number; aggregations?: any }> => {
      try {
        const response = await apiFetchOrders(body);
        console.log('[eshop] [ShopwareProvider] Orders API response:', response);
        console.log('[eshop] [ShopwareProvider] response.orders:', (response as any).orders);

        // The response structure is { orders: { elements: [...], total: N } }
        const ordersData = (response as any).orders;

        return {
          elements: ordersData?.elements || [],
          total: ordersData?.total || 0,
          aggregations: ordersData?.aggregations,
        };
      } catch (error) {
        console.error('[ShopwareProvider] Failed to fetch orders:', error);
        throw error;
      }
    },
    []
  );

  // ==================== DOCUMENT OPERATIONS ====================

  /**
   * Download a document (e.g., invoice PDF) as a Blob
   * Returns the document blob which can be used for custom handling
   */
  const downloadDocument = useCallback(async (documentId: string, deepLinkCode: string): Promise<Blob> => {
    try {
      return await apiDownloadDocument(documentId, deepLinkCode);
    } catch (error) {
      console.error('[ShopwareProvider] Failed to download document:', error);
      throw error;
    }
  }, []);

  /**
   * Trigger a document download in the browser
   * Automatically creates a download link and clicks it
   */
  const triggerDocumentDownload = useCallback(
    async (documentId: string, deepLinkCode: string, filename: string = 'document.pdf'): Promise<void> => {
      try {
        await apiTriggerDocumentDownload(documentId, deepLinkCode, filename);
      } catch (error) {
        console.error('[ShopwareProvider] Failed to trigger document download:', error);
        throw error;
      }
    },
    []
  );

  // ==================== AUTHENTICATION OPERATIONS ====================

  /**
   * Login to Shopware using SSO tokens from httpOnly cookie
   * The orthomol_sso_tokens cookie will be sent automatically
   */
  const performLogin = useCallback(async (): Promise<void> => {
    // Log in to Shopware ONCE per session. login() rotates the sw-context-token, and there are
    // several entry points that call it (PDP ensureCustomerLogin, the mein-orthomol landing island,
    // the cart page). Re-running it on every page/refresh would churn the token and, since the SSO
    // login does not merge the guest cart, drop the cart. Once a customer context is established
    // this session (marker equals the current cookie token) we reuse it and skip re-login. The
    // marker lives in localStorage (see shopwareApi) so it is shared across tabs and survives
    // navigation; it is cleared on logout.
    const establishedToken = getEstablishedCustomerToken();
    const currentToken = getContextTokenFromApi();
    if (establishedToken && currentToken && establishedToken === currentToken) {
      return;
    }

    // Guest-cart restore fallback (ORT-3109), DEFAULT OFF. When the SSO /account/login does not merge
    // the guest cart, this snapshots the guest cart before login and re-adds missing line items after.
    // It is opt-in because it races the (async, inconsistent) server-side merge and can duplicate line
    // items; the durable fix is the server-side merge (see the Valantic ticket). Enable for testing
    // via window.SHOP_GUEST_CART_LOGIN_RESTORE === true.
    const guestCartRestoreEnabled =
      typeof window !== 'undefined' && (window as any).SHOP_GUEST_CART_LOGIN_RESTORE === true;

    // Snapshot the guest cart BEFORE login (the login empties it), only when the fallback is enabled.
    const guestProducts: { referencedId: string; quantity: number }[] = [];
    const guestPromotions: string[] = [];
    if (guestCartRestoreEnabled) {
      try {
        const guestCart = await fetchCart();
        for (const li of guestCart?.lineItems ?? []) {
          if (!li.referencedId) continue;
          if (li.type === 'product') guestProducts.push({ referencedId: li.referencedId, quantity: li.quantity ?? 1 });
          else if (li.type === 'promotion') guestPromotions.push(li.referencedId);
        }
      } catch (snapshotError) {
        console.warn('[ShopwareProvider] Could not snapshot guest cart before login:', snapshotError);
      }
    }

    try {
      const response = await apiLogin();
      console.log('[eshop] [ShopwareProvider] Login successful:', response);
      // Remember the established customer token so subsequent login() calls this session (any entry
      // point) reuse it instead of re-rotating the token. Set immediately after apiLogin so it
      // covers the mein-orthomol landing and cart-page paths, not just the PDP path.
      rememberEstablishedCustomerToken(getContextTokenFromApi());
      // Refresh the cart to get the authenticated customer's cart. Guest-cart preservation is handled
      // server-side (the SSO /account/login should merge the guest cart, like standard Shopware
      // login); the client re-add below is an opt-in fallback only (default off).
      let cartData = await refreshCart();

      // Guest-cart restore fallback (opt-in, see above). Re-add guest line items the SSO login
      // dropped, matched by referencedId so a server merge that already provided them is not
      // duplicated; wait for the async server cart to settle first.
      if (guestCartRestoreEnabled && (guestProducts.length > 0 || guestPromotions.length > 0)) {
        let prevCount = -1;
        for (let i = 0; i < 8; i++) {
          const count = (cartData?.lineItems ?? []).length;
          if (count > 0 && count === prevCount) break;
          prevCount = count;
          await new Promise((r) => setTimeout(r, 300));
          try {
            cartData = await fetchCart();
          } catch {
            break;
          }
        }
        const present = new Set((cartData?.lineItems ?? []).map((li) => li.referencedId));
        const items: any[] = [
          ...guestProducts
            .filter((p) => !present.has(p.referencedId))
            .map((p) => ({ type: 'product', referencedId: p.referencedId, quantity: p.quantity })),
          ...guestPromotions
            .filter((code) => !present.has(code))
            .map((code) => ({ type: 'promotion', referencedId: code })),
        ];
        if (items.length > 0) {
          console.log('[eshop] [ShopwareProvider] Restoring', items.length, 'guest cart line item(s) dropped by SSO login');
          try {
            cartData = await apiAddToCart({ items });
            setCart(cartData);
          } catch (restoreError) {
            console.error('[ShopwareProvider] Failed to restore guest cart after login:', restoreError);
          }
        }
      }

      // ORT-3109: refresh the free-shipping threshold for the customer context. /pdp-data is only
      // called on PDPs, so in the login-after-cart-add flow (shopper adds as guest, logs in, goes
      // straight to the cart) the threshold cached in sessionStorage stays the GUEST value and the
      // cart shows the wrong free-delivery limit. Re-fetch it now with the customer token; the cart
      // itself refreshes on other paths (see ShippingProgressBar). Best-effort; never block login.
      await refreshFreeShippingThreshold(cartData?.lineItems, { withCustomerToken: true });
    } catch (error) {
      console.error('[ShopwareProvider] Failed to login:', error);
      throw error;
    }
  }, [refreshCart]);

  // One login in flight per page, whatever the entry point (useEnsureShopwareLogin on mount, the
  // add-to-cart islands' useEnsureCustomerLogin on click, the cart page). A second caller during the
  // round trip gets the first call's promise and outcome instead of a second POST /account/login,
  // which would rotate the context token again and drop the cart keyed by the first one. The guard
  // spans only the in-flight window; the once-per-session rule is the established-token check above.
  const loginInFlightRef = useRef<Promise<void> | null>(null);
  const login = useCallback((): Promise<void> => {
    if (loginInFlightRef.current) return loginInFlightRef.current;
    const inFlight = performLogin().finally(() => {
      loginInFlightRef.current = null;
    });
    loginInFlightRef.current = inFlight;
    return inFlight;
  }, [performLogin]);

  /**
   * Logout from Shopware
   * Clears the server-side session and context token
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiLogout();
      console.log('[eshop] [ShopwareProvider] Logout successful');
      // After successful logout, clear the local cart state
      setCart(null);
    } catch (error) {
      console.error('[ShopwareProvider] Failed to logout:', error);
      throw error;
    }
  }, []);

  // ==================== CUSTOMER OPERATIONS ====================

  /**
   * Fetch the current logged-in customer profile
   */
  const fetchCustomer = useCallback(async (): Promise<Customer> => {
    try {
      const response = await apiFetchCustomer();
      console.log('[eshop] [ShopwareProvider] Customer data:', response);
      return response;
    } catch (error) {
      console.error('[ShopwareProvider] Failed to fetch customer:', error);
      throw error;
    }
  }, []);

  /* refresh cart when page is restored from bfcache */
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // setIsDrawerOpened(false);
        refreshCart();
        // Release the checkout guard: the user has returned to this frozen
        // webview page (e.g. closed the external browser), so a fresh
        // checkout attempt must be allowed again.
        isProceedingToCheckoutRef.current = false;
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [refreshCart]);

  const contextValue = useMemo(
    () => ({
      // Drawer state
      isDrawerOpened,
      openDrawer,
      closeDrawer,

      // Cart state
      cart,
      isAddingToCart,
      lastAddedProductId,

      // Cart operations
      refreshCart,
      addProduct,
      addMultipleProducts,
      updateQuantity,
      removeProduct,
      clearCart,
      proceedToCheckout,

      // Product operations
      fetchProduct,
      fetchProductsByNumbers,

      // Payment methods operations
      fetchPaymentMethods,

      // Order operations
      fetchOrders,

      // Document operations
      downloadDocument,
      triggerDocumentDownload,

      // Authentication operations
      login,
      logout,

      // Customer operations
      fetchCustomer,
    }),
    [
      isDrawerOpened,
      openDrawer,
      closeDrawer,
      cart,
      isAddingToCart,
      lastAddedProductId,
      refreshCart,
      addProduct,
      addMultipleProducts,
      updateQuantity,
      removeProduct,
      clearCart,
      proceedToCheckout,
      fetchProduct,
      fetchProductsByNumbers,
      fetchPaymentMethods,
      fetchOrders,
      downloadDocument,
      triggerDocumentDownload,
      login,
      logout,
      fetchCustomer,
    ]
  );

  return <ShopwareContext.Provider value={contextValue}>{children}</ShopwareContext.Provider>;
};
